/**
 * Settings > Portal rollout > one campaign (tracker item 81): the counts
 * (waiting, sent, activated, skipped, failed, expired, bounced), the
 * activation rate, why a running campaign is held (blockedReason), and the
 * actions for its state: Start, Pause, Resume, Cancel, Send next batch and
 * "Resend to non-activated" (a follow-up round). Start, Cancel and the bulk
 * resend ask first and say what will happen. Below: the recipients with a
 * status filter, search, paging, a per-row Resend and a CSV export.
 * ADMIN and OFFICE_MANAGER.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { createAPIClient, IPortalRolloutApi } from '../../api';
import * as types from '../../api/types';
import { useAuth } from '../../context/AuthContext';
import { canManagePortalRollout, canManagePortalSettings } from '../../utils/permissions';
import { getErrorMessage } from '../../utils/apiError';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { Toast } from '../../components/Toast';
import { saveBlob } from '../settings/importExportUi';
import { RolloutBlockers } from './RolloutBlockers';
import { RolloutConfirm } from './RolloutConfirm';
import {
  CAMPAIGN_STATUS_CLASSES,
  CAMPAIGN_STATUS_LABELS,
  PORTAL_ROLLOUT_PATH,
  RECIPIENT_STATUSES,
  RECIPIENT_STATUS_CLASSES,
  RECIPIENT_STATUS_LABELS,
  activationRateText,
  campaignSummaryText,
  dateTimeText,
  isStateConflict,
  nextBatchText,
  notQueuedText,
  plural,
  resendBlockedLabel,
  rolloutErrorText,
  skipReasonLabel,
} from './portalRolloutUi';

const PAGE_SIZE = 50;
/** A running campaign refreshes itself this often. */
export const DETAIL_REFRESH_MS = 30_000;

type Confirm = 'start' | 'cancel' | 'resend' | null;

export function PortalRolloutDetailPage() {
  const { campaignId = '' } = useParams();
  const { user } = useAuth();
  const location = useLocation();
  const api = useMemo<IPortalRolloutApi>(() => createAPIClient(), []);
  const allowed = canManagePortalRollout(user);
  const isAdmin = canManagePortalSettings(user);
  const [params, setParams] = useSearchParams();
  const rawStatus = params.get('status') ?? '';
  const recipientStatus = (RECIPIENT_STATUSES as string[]).includes(rawStatus) ? (rawStatus as types.RolloutRecipientStatus) : '';
  const search = params.get('q') ?? '';
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);

  const [detail, setDetail] = useState<types.RolloutCampaignDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState(search);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [busy, setBusy] = useState(false);
  const [busyRecipient, setBusyRecipient] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(() => ((location.state as { notice?: string | null } | null)?.notice) ?? null);
  const [readiness, setReadiness] = useState<types.RolloutReadiness | null>(null);
  const [exporting, setExporting] = useState(false);
  const loadId = useRef(0);

  const load = useCallback(async (quiet = false) => {
    const id = ++loadId.current;
    if (!quiet) setLoadError(null);
    try {
      const data = await api.getPortalRolloutCampaign(campaignId, { recipientStatus: recipientStatus || undefined, search: search || undefined, page, limit: PAGE_SIZE });
      if (id === loadId.current) setDetail(data);
    } catch (cause) {
      if (id === loadId.current && !quiet) setLoadError(getErrorMessage(cause, 'Could not load the campaign'));
    }
  }, [api, campaignId, recipientStatus, search, page]);

  useEffect(() => {
    if (!allowed) return;
    void load();
  }, [allowed, load]);

  const status = detail?.campaign.status;
  useEffect(() => {
    if (!allowed || status !== 'RUNNING') return undefined;
    const timer = setInterval(() => { void load(true); }, DETAIL_REFRESH_MS);
    return () => clearInterval(timer);
  }, [allowed, status, load]);

  useEffect(() => {
    if (!allowed) return undefined;
    let active = true;
    api.getPortalRolloutReadiness().then((data) => { if (active) setReadiness(data); }).catch(() => { /* the campaign's own blockedReason still shows */ });
    return () => { active = false; };
  }, [api, allowed, detail?.campaign.status]);

  if (!allowed) return <p className="p-4 text-sm text-gray-600">Portal rollout is available to admins and office managers.</p>;

  const change = (next: Record<string, string>) => {
    const updated = new URLSearchParams(params);
    Object.entries(next).forEach(([key, value]) => (value ? updated.set(key, value) : updated.delete(key)));
    if (!('page' in next)) updated.delete('page');
    setParams(updated);
  };

  /** Run one campaign action; a state conflict reloads the campaign so the buttons match. */
  async function act(work: () => Promise<string | void>, failure: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const message = await work();
      setConfirm(null);
      if (message) setNotice(message);
      await load(true);
    } catch (cause) {
      setConfirm(null);
      setError(rolloutErrorText(cause, failure));
      if (isStateConflict(cause)) await load(true);
    } finally {
      setBusy(false);
    }
  }

  async function exportCsv() {
    setExporting(true);
    setError(null);
    try {
      const file = await api.exportPortalRolloutRecipients(campaignId);
      saveBlob(file.blob, file.filename);
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not export the recipients'));
    } finally {
      setExporting(false);
    }
  }

  async function resendOne(recipient: types.RolloutRecipient) {
    setBusyRecipient(recipient.id);
    setError(null);
    setNotice(null);
    try {
      await api.resendPortalRolloutRecipient(campaignId, recipient.id);
      setNotice(`A new invitation was sent to ${recipient.email ?? recipient.customerName}. The previous link no longer works.`);
      await load(true);
    } catch (cause) {
      setError(rolloutErrorText(cause, 'Could not resend the invitation'));
      await load(true);
    } finally {
      setBusyRecipient(null);
    }
  }

  const campaign = detail?.campaign ?? null;
  const recipients = detail?.recipients ?? null;
  const c = campaign?.counts;
  const canFollowUp = !!campaign && (campaign.status === 'RUNNING' || campaign.status === 'PAUSED' || campaign.status === 'COMPLETED');
  const followUpCount = c ? c.sent + c.expired : 0;
  const blockers = campaign && (campaign.status === 'DRAFT' || campaign.status === 'PAUSED' || campaign.blockedReason) ? readiness?.blockers ?? [] : [];

  return (
    <main className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      <p className="text-sm"><Link to={PORTAL_ROLLOUT_PATH} className="inline-block py-2 text-blue-700 underline">Portal rollout</Link></p>
      {loadError && (
        <p role="alert" className="mt-3 rounded bg-red-50 p-3 text-sm text-red-800">
          {loadError} <button type="button" className="underline" onClick={() => void load()}>Try again</button>
        </p>
      )}
      {!campaign && !loadError && <p role="status" className="mt-4 text-sm text-gray-500">Loading campaign…</p>}

      {campaign && c && (
        <>
          <div className="mt-1 flex flex-wrap items-start justify-between gap-2">
            <h1 className="min-w-0 break-words text-2xl font-bold text-gray-900">{campaign.name}</h1>
            <span className={`rounded px-2 py-0.5 text-sm font-semibold ${CAMPAIGN_STATUS_CLASSES[campaign.status]}`} data-testid="campaign-status">{CAMPAIGN_STATUS_LABELS[campaign.status]}</span>
          </div>
          <p className="mt-1 text-sm text-gray-600">{campaignSummaryText(campaign)}</p>
          {nextBatchText(campaign) && <p className="text-xs text-gray-500">{nextBatchText(campaign)}</p>}
          <p className="mt-1 text-xs text-gray-500">
            Batches of {campaign.batchSize}, {campaign.batchGapMinutes} minutes apart. Follow-ups wait {campaign.minResendDays} days and stop after {campaign.maxResends}.
            {campaign.currentRound > 1 ? ` Round ${campaign.currentRound}.` : ''}
          </p>

          {notice && <div className="mt-3"><Toast kind="success">{notice}</Toast></div>}
          {error && <p role="alert" className="mt-3 rounded bg-red-50 p-3 text-sm text-red-800">{error}</p>}

          {campaign.blockedReason && (
            <div role="alert" className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="blocked-notice">
              <p className="font-semibold">Sending is on hold</p>
              <p className="mt-1">{campaign.blockedReasonText ?? campaign.blockedReason} The campaign keeps going by itself once this is fixed.</p>
            </div>
          )}
          {!campaign.blockedReason && blockers.length > 0 && <div className="mt-4"><RolloutBlockers blockers={blockers} isAdmin={isAdmin} heading={campaign.status === 'DRAFT' ? 'This campaign cannot start yet' : 'This campaign cannot resume yet'} /></div>}
          {campaign.blockedReason && blockers.length > 0 && <div className="mt-3"><RolloutBlockers blockers={blockers} isAdmin={isAdmin} heading="How to fix it" /></div>}

          <section aria-label="Progress" className="mt-5">
            <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
              <p className="text-sm text-gray-600">Activation rate</p>
              <p className="text-3xl font-bold text-gray-900" data-testid="activation-rate">{activationRateText(campaign.activationRate)}</p>
              <p className="text-xs text-gray-500">{c.invited === 0 ? 'Nothing has been sent yet.' : `${plural(c.activated, 'customer')} set a password out of ${plural(c.invited, 'customer')} invited.`}</p>
            </div>
            <dl className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4" aria-label="Counts">
              {([
                ['PENDING', c.pending], ['SENT', c.sent], ['ACTIVATED', c.activated], ['SKIPPED', c.skipped],
                ['FAILED', c.failed], ['EXPIRED', c.expired], ['BOUNCED', c.bounced],
              ] as Array<[types.RolloutRecipientStatus, number]>).map(([key, value]) => (
                <div key={key} className="rounded bg-white p-3 shadow-sm ring-1 ring-gray-200" data-count={key}>
                  <dt className="text-xs text-gray-600">{RECIPIENT_STATUS_LABELS[key]}</dt>
                  <dd className="text-xl font-bold text-gray-900">
                    <button type="button" className="inline-block min-h-[44px] min-w-[44px] text-left underline decoration-dotted sm:min-h-0 sm:min-w-0" aria-label={`Show ${RECIPIENT_STATUS_LABELS[key].toLowerCase()} (${value})`} onClick={() => change({ status: key })}>{value.toLocaleString()}</button>
                  </dd>
                </div>
              ))}
              <div className="rounded bg-white p-3 shadow-sm ring-1 ring-gray-200" data-count="TOTAL">
                <dt className="text-xs text-gray-600">All customers</dt>
                <dd className="text-xl font-bold text-gray-900">{c.total.toLocaleString()}</dd>
              </div>
            </dl>
            {c.resendQueued > 0 && <p className="mt-2 text-sm text-gray-700">{plural(c.resendQueued, 'follow-up')} queued for the next batch.</p>}
          </section>

          {confirm === 'start' && (
            <RolloutConfirm label="Confirm start" confirmLabel={`Yes, start (${plural(c.pending, 'email')})`} busy={busy} onConfirm={() => void act(async () => { await api.startPortalRolloutCampaign(campaign.id); return 'The campaign is running.'; }, 'Could not start the campaign')} onCancel={() => setConfirm(null)}>
              <p><strong>{plural(c.pending, 'email')}</strong> will go out in batches of up to {campaign.batchSize}, about {campaign.batchGapMinutes} minutes apart.</p>
              <p>Each customer gets a portal login and a set-password link that works for 7 days. Customers who opted out are never emailed.</p>
            </RolloutConfirm>
          )}
          {confirm === 'cancel' && (
            <RolloutConfirm label="Confirm cancel" confirmLabel="Yes, cancel the campaign" cancelLabel="Keep it" busy={busy} onConfirm={() => void act(async () => { await api.cancelPortalRolloutCampaign(campaign.id); return 'The campaign was cancelled.'; }, 'Could not cancel the campaign')} onCancel={() => setConfirm(null)}>
              <p>{c.pending + c.resendQueued > 0 ? `The ${plural(c.pending + c.resendQueued, 'invitation')} still waiting will not be sent.` : 'Nothing more will be sent.'} This cannot be undone.</p>
              <p><strong>Portal logins already created stay</strong>, and links already emailed keep working until they expire.</p>
            </RolloutConfirm>
          )}
          {confirm === 'resend' && (
            <RolloutConfirm
              label="Confirm resend"
              confirmLabel={`Yes, resend to up to ${plural(followUpCount, 'customer')}`}
              busy={busy}
              onConfirm={() => void act(async () => {
                const result = await api.resendPortalRolloutCampaign(campaign.id);
                const left = notQueuedText(result.notQueued);
                return `${plural(result.queued, 'follow-up')} queued for the next batch${left ? `; left out: ${left}` : ''}.`;
              }, 'Could not queue the follow-up')}
              onCancel={() => setConfirm(null)}
            >
              <p>Send a new invitation to up to <strong>{plural(followUpCount, 'customer')}</strong> who have not activated (sent or link expired). The new link replaces the old one.</p>
              <p>
                Only customers last emailed at least {campaign.minResendDays} days ago and emailed fewer than {1 + campaign.maxResends} times are included
                ({campaign.maxResends === 0 ? 'no follow-ups are allowed by this campaign' : `at most ${plural(campaign.maxResends, 'follow-up')} each`}). Opted-out customers, bounced addresses and customers who got another login are left out.
              </p>
            </RolloutConfirm>
          )}

          {!confirm && (
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap" role="group" aria-label="Campaign actions">
              {campaign.status === 'DRAFT' && <button type="button" className={primaryButtonClass} disabled={busy || c.pending === 0} onClick={() => setConfirm('start')}>Start campaign</button>}
              {campaign.status === 'PAUSED' && <button type="button" className={primaryButtonClass} disabled={busy} onClick={() => void act(async () => { await api.resumePortalRolloutCampaign(campaign.id); return 'The campaign is running again.'; }, 'Could not resume the campaign')}>Resume</button>}
              {campaign.status === 'RUNNING' && (
                <button
                  type="button" className={primaryButtonClass} disabled={busy || !!campaign.blockedReason}
                  onClick={() => void act(async () => {
                    const { batch } = await api.sendPortalRolloutBatch(campaign.id);
                    return `Batch done: ${plural(batch.sent, 'invitation')} sent${batch.skipped ? `, ${batch.skipped} skipped` : ''}${batch.failed ? `, ${batch.failed} failed` : ''}${batch.limitedByRate ? '. The batch was cut to the sending limit' : ''}.`;
                  }, 'Could not send the batch')}
                >
                  Send next batch
                </button>
              )}
              {campaign.status === 'RUNNING' && <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => void act(async () => { await api.pausePortalRolloutCampaign(campaign.id); return 'The campaign is paused.'; }, 'Could not pause the campaign')}>Pause</button>}
              {canFollowUp && <button type="button" className={secondaryButtonClass} disabled={busy || followUpCount === 0} onClick={() => setConfirm('resend')}>Resend to non-activated</button>}
              {(campaign.status === 'DRAFT' || campaign.status === 'RUNNING' || campaign.status === 'PAUSED') && <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => setConfirm('cancel')}>Cancel campaign</button>}
              <button type="button" className={secondaryButtonClass} disabled={exporting} onClick={() => void exportCsv()}>{exporting ? 'Preparing…' : 'Export CSV'}</button>
            </div>
          )}

          <section aria-labelledby="recipients-heading" className="mt-8">
            <h2 id="recipients-heading" className="text-lg font-semibold text-gray-900">Customers</h2>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[14rem_1fr] sm:items-end">
              <div>
                <label htmlFor="recipient-status" className={labelClass}>Status</label>
                <select id="recipient-status" className={inputClass} value={recipientStatus} onChange={(e) => change({ status: e.target.value })}>
                  <option value="">All</option>
                  {RECIPIENT_STATUSES.map((s) => <option key={s} value={s}>{RECIPIENT_STATUS_LABELS[s]}</option>)}
                </select>
              </div>
              <form className="flex flex-col gap-2 sm:flex-row sm:items-end" role="search" onSubmit={(e) => { e.preventDefault(); change({ q: searchText.trim() }); }}>
                <div className="min-w-0 flex-1">
                  <label htmlFor="recipient-search" className={labelClass}>Search name or email</label>
                  <input id="recipient-search" type="search" className={inputClass} value={searchText} onChange={(e) => setSearchText(e.target.value)} />
                </div>
                <button type="submit" className={secondaryButtonClass}>Search</button>
              </form>
            </div>

            {!recipients && <p role="status" className="mt-4 text-sm text-gray-500">Loading customers…</p>}
            {recipients && recipients.data.length === 0 && (
              <p className="mt-4 rounded border border-dashed border-gray-300 bg-white p-4 text-sm text-gray-600" data-testid="no-recipients">
                {recipientStatus || search ? 'No customer matches these filters.' : 'This campaign has no customers.'}
              </p>
            )}
            {recipients && recipients.data.length > 0 && (
              <ul className="mt-4 divide-y divide-gray-100 rounded-lg bg-white shadow-sm ring-1 ring-gray-200" aria-label="Campaign customers">
                {recipients.data.map((r) => {
                  const resendable = ['SENT', 'EXPIRED', 'FAILED'].includes(r.status);
                  return (
                    <li key={r.id} className="p-3 sm:p-4" data-testid="recipient-row" data-status={r.status}>
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                          <Link to={`/customers/${encodeURIComponent(r.customerId)}`} className="inline-block break-words py-1 font-medium text-blue-700 underline">{r.customerName}</Link>
                          <p className="break-all text-xs text-gray-500">{r.email ?? 'No email address'}</p>
                        </div>
                        <span className={`rounded px-2 py-0.5 text-xs font-semibold ${RECIPIENT_STATUS_CLASSES[r.status]}`}>{RECIPIENT_STATUS_LABELS[r.status]}</span>
                      </div>
                      <p className="mt-1 text-xs text-gray-600">
                        {r.status === 'SKIPPED' && <span>{skipReasonLabel(r.skipReason, r.skipReasonText)}. </span>}
                        {r.sendCount > 0 && <span>Sent {plural(r.sendCount, 'time')}, last {dateTimeText(r.lastSentAt)}. </span>}
                        {r.status === 'SENT' && r.inviteExpiresAt && <span>Link works until {dateTimeText(r.inviteExpiresAt)}. </span>}
                        {r.status === 'EXPIRED' && <span>Link expired {dateTimeText(r.inviteExpiresAt)}. </span>}
                        {r.status === 'ACTIVATED' && <span>Set a password {dateTimeText(r.activatedAt)}. </span>}
                        {r.status === 'BOUNCED' && <span>Bounced {dateTimeText(r.bouncedAt)}. </span>}
                        {r.status === 'FAILED' && r.lastError && <span>{r.lastError}. </span>}
                        {r.resendQueued && <span className="font-semibold">Follow-up queued. </span>}
                      </p>
                      {resendable && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          {r.canResend ? (
                            <button type="button" className={secondaryButtonClass} disabled={busyRecipient === r.id || busy} onClick={() => void resendOne(r)} aria-label={`Resend to ${r.customerName}`}>
                              {busyRecipient === r.id ? 'Sending…' : 'Resend'}
                            </button>
                          ) : (
                            r.resendBlockedReason && <span className="text-xs text-gray-500">{resendBlockedLabel(r.resendBlockedReason)}</span>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            {recipients && (
              <p className="mt-2 text-xs text-gray-500">{plural(recipients.pagination.total, 'customer')}{recipientStatus || search ? ' match' : ''}.</p>
            )}
            {recipients && recipients.pagination.pages > 1 && (
              <nav className="mt-3 flex items-center justify-between text-sm" aria-label="Pages">
                <button type="button" className={secondaryButtonClass} disabled={page <= 1} onClick={() => change({ page: String(page - 1) })}>Previous</button>
                <span>Page {recipients.pagination.page} of {recipients.pagination.pages}</span>
                <button type="button" className={secondaryButtonClass} disabled={page >= recipients.pagination.pages} onClick={() => change({ page: String(page + 1) })}>Next</button>
              </nav>
            )}
          </section>
        </>
      )}
    </main>
  );
}
